import { useState, useEffect } from "react";
import { type Site, type Bot, api } from "../api";
import {
  MKDialog,
  MKDialogContent,
  MKDialogHeader,
  MKDialogTitle,
  MKDialogFooter,
  MKButton,
  MKInput,
  MKTextarea,
  MKLabel,
  MKCheckbox,
} from "@mytraai/mytrakit";

interface Props {
  site?: Site;
  onSave: (site: Site) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

interface BotFormData {
  bot_id?: number;
  name: string;
  kubeconfig: string;
}

export default function SiteEditor({
  site,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const [name, setName] = useState(site?.name || "");
  const [kubeconfig, setKubeconfig] = useState("");
  const [fluxRepoUrl, setFluxRepoUrl] = useState(site?.flux_repo_url || "");
  const [isProduction, setIsProduction] = useState(
    site?.is_production || false,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  // Bot management state
  const [bots, setBots] = useState<Bot[]>([]);
  const [editingBot, setEditingBot] = useState<BotFormData | null>(null);
  const [savingBot, setSavingBot] = useState(false);
  const [deletingBotId, setDeletingBotId] = useState<number | null>(null);

  useEffect(() => {
    if (site) {
      api.listBots(site.site_id).then(setBots).catch(console.error);
    }
  }, [site]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const data = {
        name: name.trim(),
        kubeconfig: kubeconfig || undefined,
        flux_repo_url: fluxRepoUrl || undefined,
        is_production: isProduction,
      };

      const saved = site
        ? await api.updateSite(site.site_id, data)
        : await api.createSite(data);

      onSave(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save site");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!site || !onDelete) return;

    setDeleting(true);
    setError("");

    try {
      await api.deleteSite(site.site_id);
      onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete site");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveBot = async () => {
    if (!site || !editingBot) return;

    setSavingBot(true);
    setError("");

    try {
      const data = { name: editingBot.name, kubeconfig: editingBot.kubeconfig };
      if (editingBot.bot_id) {
        await api.updateBot(site.site_id, editingBot.bot_id, data);
      } else {
        await api.createBot(site.site_id, data);
      }
      const updatedBots = await api.listBots(site.site_id);
      setBots(updatedBots);
      setEditingBot(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save bot");
    } finally {
      setSavingBot(false);
    }
  };

  const handleDeleteBot = async (botId: number) => {
    if (!site) return;

    setDeletingBotId(botId);
    setError("");

    try {
      await api.deleteBot(site.site_id, botId);
      setBots(bots.filter((b) => b.bot_id !== botId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete bot");
    } finally {
      setDeletingBotId(null);
    }
  };

  return (
    <MKDialog open onOpenChange={(open) => !open && onCancel()}>
      <MKDialogContent>
        <form onSubmit={handleSubmit}>
          <MKDialogHeader>
            <MKDialogTitle>{site ? "Edit Site" : "Add Site"}</MKDialogTitle>
          </MKDialogHeader>

          <div className="py-4 space-y-4">
            {error && (
              <div className="bg-surface-destructive/10 text-content-destructive px-4 py-2 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <MKLabel htmlFor="name">Name</MKLabel>
              <MKInput
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., production, staging"
              />
            </div>

            <div className="space-y-2">
              <MKLabel htmlFor="kubeconfig">Kubeconfig</MKLabel>
              <MKTextarea
                id="kubeconfig"
                value={kubeconfig}
                onChange={(e) => setKubeconfig(e.target.value)}
                className="font-mono text-sm"
                rows={4}
                placeholder="Paste kubeconfig YAML here..."
              />
              {site && (
                <p className="text-xs text-content-tertiary">
                  Leave blank to keep existing kubeconfig
                </p>
              )}
            </div>

            <div className="space-y-2">
              <MKLabel htmlFor="flux_repo_url">Flux Repository URL</MKLabel>
              <MKInput
                id="flux_repo_url"
                value={fluxRepoUrl}
                onChange={(e) => setFluxRepoUrl(e.target.value)}
                placeholder="https://github.com/org/repo"
              />
            </div>

            <div className="flex items-center gap-2">
              <MKCheckbox
                id="is_production"
                checked={isProduction}
                onCheckedChange={(checked) => setIsProduction(checked === true)}
              />
              <MKLabel htmlFor="is_production" className="font-normal">
                Production environment
              </MKLabel>
            </div>
            {isProduction && (
              <p className="text-xs text-content-warning ml-6">
                Deploying to production will require extra confirmation
              </p>
            )}

            {site && (
              <div className="space-y-2 pt-4 border-t border-border-secondary">
                <div className="flex items-center justify-between">
                  <MKLabel>Bots</MKLabel>
                  <MKButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingBot({ name: "", kubeconfig: "" })}
                  >
                    Add Bot
                  </MKButton>
                </div>

                {bots.length === 0 && !editingBot && (
                  <p className="text-sm text-content-tertiary">
                    No bots configured
                  </p>
                )}

                {bots.length > 0 && (
                  <div className="space-y-2">
                    {bots.map((bot) => (
                      <div
                        key={bot.bot_id}
                        className="flex items-center justify-between p-2 bg-surface-secondary rounded"
                      >
                        <span className="text-sm">{bot.name}</span>
                        <div className="flex gap-2">
                          <MKButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setEditingBot({
                                bot_id: bot.bot_id,
                                name: bot.name,
                                kubeconfig: "",
                              })
                            }
                          >
                            Edit
                          </MKButton>
                          <MKButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBot(bot.bot_id)}
                            disabled={deletingBotId === bot.bot_id}
                          >
                            {deletingBotId === bot.bot_id
                              ? "Deleting..."
                              : "Delete"}
                          </MKButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {editingBot && (
                  <div className="space-y-2 p-3 bg-surface-secondary rounded">
                    <MKInput
                      placeholder="Bot name"
                      value={editingBot.name}
                      onChange={(e) =>
                        setEditingBot({ ...editingBot, name: e.target.value })
                      }
                    />
                    <MKTextarea
                      placeholder="Kubeconfig YAML..."
                      className="font-mono text-sm"
                      rows={3}
                      value={editingBot.kubeconfig}
                      onChange={(e) =>
                        setEditingBot({
                          ...editingBot,
                          kubeconfig: e.target.value,
                        })
                      }
                    />
                    {editingBot.bot_id && (
                      <p className="text-xs text-content-tertiary">
                        Leave kubeconfig blank to keep existing
                      </p>
                    )}
                    <div className="flex gap-2 justify-end">
                      <MKButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingBot(null)}
                      >
                        Cancel
                      </MKButton>
                      <MKButton
                        type="button"
                        variant="brand"
                        size="sm"
                        onClick={handleSaveBot}
                        disabled={
                          savingBot ||
                          !editingBot.name.trim() ||
                          (!editingBot.bot_id && !editingBot.kubeconfig.trim())
                        }
                      >
                        {savingBot ? "Saving..." : "CEMENT Bot"}
                      </MKButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <MKDialogFooter>
            {site && onDelete && (
              <MKButton
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isProduction || deleting || saving}
                className="mr-auto"
              >
                {deleting ? "Deleting..." : "Delete"}
              </MKButton>
            )}
            <MKButton
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={saving || deleting}
            >
              Cancel
            </MKButton>
            <MKButton
              type="submit"
              variant="brand"
              disabled={saving || deleting}
              data-test-id="site-edit-save"
            >
              {saving ? "Saving..." : "Save"}
            </MKButton>
          </MKDialogFooter>
        </form>
      </MKDialogContent>
    </MKDialog>
  );
}
